import React, { useEffect, useState } from 'react';
import { FlagIcon, BuildingIcon, HomeIcon } from 'lucide-react';
import { EntityScope } from '../CurrencyExchangeView';
interface EntitySiteSelectorProps {
  scope: EntityScope;
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
  onChange: (entityId: string | undefined, entityName: string | undefined, siteId: string | undefined, siteName: string | undefined, scope: EntityScope) => void;
  locale: string;
}
interface Entity {
  id: string;
  name: string;
  sites: Site[];
}
interface Site {
  id: string;
  name: string;
  entityId: string;
}
export function EntitySiteSelector({
  scope,
  entityId,
  entityName,
  siteId,
  siteName,
  onChange,
  locale
}: EntitySiteSelectorProps) {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  // Load sample entities and sites
  useEffect(() => {
    // In a real app, this would be an API call
    const sampleEntities: Entity[] = [{
      id: 'entity-001',
      name: 'Corporate',
      sites: [{
        id: 'site-001',
        name: 'Headquarters',
        entityId: 'entity-001'
      }, {
        id: 'site-002',
        name: 'R&D Center',
        entityId: 'entity-001'
      }, {
        id: 'site-003',
        name: 'European Office',
        entityId: 'entity-001'
      }]
    }, {
      id: 'entity-002',
      name: 'LATAM Operations',
      sites: [{
        id: 'site-004',
        name: 'Mexico City',
        entityId: 'entity-002'
      }, {
        id: 'site-005',
        name: 'São Paulo',
        entityId: 'entity-002'
      }]
    }, {
      id: 'entity-003',
      name: 'APAC Division',
      sites: [{
        id: 'site-006',
        name: 'Singapore',
        entityId: 'entity-003'
      }, {
        id: 'site-007',
        name: 'Tokyo',
        entityId: 'entity-003'
      }, {
        id: 'site-008',
        name: 'Sydney',
        entityId: 'entity-003'
      }]
    }];
    setEntities(sampleEntities);
    // If we have an entityId, find and set the selected entity
    if (entityId) {
      const entity = sampleEntities.find(e => e.id === entityId);
      if (entity) {
        setSelectedEntity(entity);
        setSites(entity.sites);
      }
    }
  }, [entityId]);
  // Handle scope change
  const handleScopeChange = (newScope: EntityScope) => {
    onChange(newScope === 'Global' ? undefined : entityId, newScope === 'Global' ? undefined : entityName, newScope === 'Global' || newScope === 'Entity' ? undefined : siteId, newScope === 'Global' || newScope === 'Entity' ? undefined : siteName, newScope);
  };
  // Handle entity selection
  const handleEntityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedEntityId = e.target.value;
    if (!selectedEntityId) {
      setSelectedEntity(null);
      setSites([]);
      onChange(undefined, undefined, undefined, undefined, 'Global');
      return;
    }
    const entity = entities.find(e => e.id === selectedEntityId);
    if (entity) {
      setSelectedEntity(entity);
      setSites(entity.sites);
      onChange(entity.id, entity.name, undefined, undefined, 'Entity');
    }
  };
  // Handle site selection
  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSiteId = e.target.value;
    if (!selectedSiteId || !selectedEntity) {
      onChange(selectedEntity?.id, selectedEntity?.name, undefined, undefined, 'Entity');
      return;
    }
    const site = sites.find(s => s.id === selectedSiteId);
    if (site) {
      onChange(selectedEntity.id, selectedEntity.name, site.id, site.name, 'Site');
    }
  };
  return <div className="space-y-4" data-id="element-7692">
      <div data-id="element-7693">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7694">
          Rate Scope
        </label>
        <div className="flex flex-wrap gap-2" data-id="element-7695">
          <button type="button" onClick={() => handleScopeChange('Global')} className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md ${scope === 'Global' ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-7696">
            <FlagIcon className="h-4 w-4 mr-1" data-id="element-7697" />
            Global
          </button>
          <button type="button" onClick={() => handleScopeChange('Entity')} className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md ${scope === 'Entity' ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-7698">
            <BuildingIcon className="h-4 w-4 mr-1" data-id="element-7699" />
            Entity
          </button>
          <button type="button" onClick={() => handleScopeChange('Site')} className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md ${scope === 'Site' ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-7700">
            <HomeIcon className="h-4 w-4 mr-1" data-id="element-7701" />
            Site
          </button>
        </div>
      </div>
      {/* Entity Selector - shown for Entity and Site scopes */}
      {(scope === 'Entity' || scope === 'Site') && <div data-id="element-7702">
          <label htmlFor="entity" className="block text-sm font-medium text-gray-700" data-id="element-7703">
            Entity
          </label>
          <select id="entity" name="entity" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={entityId || ''} onChange={handleEntityChange} required data-id="element-7704">
            <option value="" data-id="element-7705">Select an entity</option>
            {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-7706">
                {entity.name}
              </option>)}
          </select>
        </div>}
      {/* Site Selector - shown only for Site scope */}
      {scope === 'Site' && <div data-id="element-7707">
          <label htmlFor="site" className="block text-sm font-medium text-gray-700" data-id="element-7708">
            Site
          </label>
          <select id="site" name="site" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={siteId || ''} onChange={handleSiteChange} required disabled={!selectedEntity} data-id="element-7709">
            <option value="" data-id="element-7710">Select a site</option>
            {sites.map(site => <option key={site.id} value={site.id} data-id="element-7711">
                {site.name}
              </option>)}
          </select>
          {!selectedEntity && <p className="mt-1 text-xs text-red-500" data-id="element-7712">
              Please select an entity first
            </p>}
        </div>}
    </div>;
}